import { z } from 'zod';
import { memberIdSchema } from '../../identity/legacy-identity-id';
import { apiEnvelope } from '../../envelope';
import { checkInDateSchema } from '../../check-ins/schema';
import {
  bamAnswerValueSchema,
  bamAssessmentStatusSchema,
  bamContextSnapshotSchema,
  bamDefinitionSchema,
  bamQuestionTypeSchema,
  bamResponsesSchema,
} from '../../bam/schema';

// Wire envelopes for `/api/v1/bam`. Per ADR-066 the wire convention is
// camelCase; the stored jsonb keys (question `name`s inside `responses`, and
// the `definition` content) stay as-authored and are exempt.

export const bamQuestionnaireDataSchema = z
  .object({
    questionnaireId: z.string().uuid(),
    slug: z.string().min(1),
    version: z.number().int(),
    title: z.string().min(1),
    definition: bamDefinitionSchema,
  })
  .strict();
export type BamQuestionnaireData = z.infer<typeof bamQuestionnaireDataSchema>;
export const bamQuestionnaireResponseSchema = apiEnvelope(bamQuestionnaireDataSchema);

export const bamAssessmentSchema = z
  .object({
    assessmentId: z.string().uuid(),
    questionnaireId: z.string().uuid(),
    slug: z.string().min(1),
    version: z.number().int(),
    status: bamAssessmentStatusSchema,
    startedAt: z.string().datetime(),
    completedAt: z.string().datetime().nullable(),
  })
  .strict();
export type BamAssessment = z.infer<typeof bamAssessmentSchema>;

export const startBamRequestSchema = z.object({}).strict();
export type StartBamRequest = z.infer<typeof startBamRequestSchema>;

export const bamAttemptDataSchema = z
  .object({
    assessment: bamAssessmentSchema,
    responses: bamResponsesSchema,
  })
  .strict();
export type BamAttemptData = z.infer<typeof bamAttemptDataSchema>;
export const bamAttemptResponseSchema = apiEnvelope(bamAttemptDataSchema);

export const activeBamDataSchema = z
  .object({
    assessment: bamAssessmentSchema.nullable(),
    responses: bamResponsesSchema,
  })
  .strict();
export type ActiveBamData = z.infer<typeof activeBamDataSchema>;
export const activeBamResponseSchema = apiEnvelope(activeBamDataSchema);

// Only short string values select wording, so nothing larger is accepted.
export const bamShownContextSchema = z
  .record(z.string().min(1).max(64), z.string().max(64))
  .refine((context) => Object.keys(context).length <= 16, {
    message: 'too many context values',
  });
export type BamShownContext = z.infer<typeof bamShownContextSchema>;

export const saveBamAnswerRequestSchema = z
  .object({
    assessmentId: z.string().uuid(),
    questionName: z.string().min(1),
    answerValue: bamAnswerValueSchema,
    // The wording-selecting context values the client was showing when the
    // answer was chosen, so the server can record the wording the member
    // actually saw. Absent from older clients, which leave the stored context
    // unchanged.
    shownContext: bamShownContextSchema.optional(),
  })
  .strict();
export type SaveBamAnswerRequest = z.infer<typeof saveBamAnswerRequestSchema>;

export const saveBamAnswerDataSchema = z
  .object({
    assessment: bamAssessmentSchema,
    savedAt: z.string().datetime(),
  })
  .strict();
export type SaveBamAnswerData = z.infer<typeof saveBamAnswerDataSchema>;
export const saveBamAnswerResponseSchema = apiEnvelope(saveBamAnswerDataSchema);

export const completeBamDataSchema = z
  .object({
    assessment: bamAssessmentSchema,
  })
  .strict();
export type CompleteBamData = z.infer<typeof completeBamDataSchema>;
export const completeBamResponseSchema = apiEnvelope(completeBamDataSchema);

// `GET /api/v1/bam/{id}/context` — the member context resolved for one
// attempt, snapshotted on first read. Deliberately not `.strict()` (ADR-135
// Rule 1): a later field must not break a client parsing an older shape.
export const bamContextDataSchema = z.object({
  context: bamContextSnapshotSchema,
});
export type BamContextData = z.infer<typeof bamContextDataSchema>;
export const bamContextResponseSchema = apiEnvelope(bamContextDataSchema);

// ── Staff read surface (`/api/v1/internal/bam`) ───────────────────────────────
// Care-team reads of a member's completed BAM check-ins on real data. Raw items
// only — the server resolves each stored answer against the assessment's pinned
// questionnaire `definition` and returns the server-authored prompt + option
// label, so the client stays condition-agnostic (ADR-015). Subscale scoring is a
// separate, deferred follow-up: NO option `value`/score field appears anywhere
// on this surface.
// Atlas parses these response schemas, so neither they nor their nested objects
// reject unknown keys (ADR-135 Rule 1); the server still validates its own
// output shape through `sendOk`.

// One row in a member's completed-assessment history: identity + the pinned
// questionnaire it belongs to. No responses — the detail read carries those.
export const bamCompletedAssessmentSummarySchema = z
  .object({
    assessmentId: z.string().uuid(),
    questionnaireId: z.string().uuid(),
    slug: z.string().min(1),
    version: z.number().int(),
    completedAt: z.string().datetime(),
    // The member-local calendar day of completion (`YYYY-MM-DD`), decided
    // server-side from the member's resolved zone and transported as a fixed
    // string (ADR-096 Rule 9) — never re-derived from `completedAt` at the far
    // end, where a staff viewer's own zone (or UTC) would shift it off the day
    // the member actually checked in on. `completedAt` stays the raw instant.
    completedDate: checkInDateSchema,
  })
  .strict();
export type BamCompletedAssessmentSummary = z.infer<typeof bamCompletedAssessmentSummarySchema>;

// One resolved item: the server-authored prompt for the question plus the
// answer resolved against the pinned definition. `answer` is null when the
// question is unanswered or the stored value no longer maps to a definition
// option (stale-definition safety — no fabricated label). A resolved answer
// carries only display fields (`key`, `label`) — never a score/`value`.
// `skipped` marks an item the definition's skip rule removed from the
// member's path: its `answer` is then the option the rule implies (or null),
// never something the member chose. The server always emits it; it is
// optional here only so a newer Atlas bundle can read an older server during
// a deploy (ADR-135), and absence reads as false. Atlas parses this schema, so
// it tolerates unknown keys (ADR-135 Rule 1).
export const bamResolvedItemSchema = z
  .object({
    questionName: z.string().min(1),
    prompt: z.string().min(1),
    type: bamQuestionTypeSchema,
    answer: z
      .object({
        key: z.string().nullable(),
        label: z.string().min(1),
      })
      .nullable(),
    skipped: z.boolean().optional(),
  })
  // The option `key` nullability is fully determined by the question type: a
  // `single_select` answer names the chosen option key, a `number` answer never
  // carries one. (An unanswered / stale answer is the whole-object `null` case
  // above and is unconstrained here.) Enforcing both directions keeps this
  // shared schema an exact contract, so a drifted payload is rejected on both
  // the server (`sendOk`) and the Atlas parser rather than silently accepted.
  .superRefine((item, ctx) => {
    if (item.answer === null) return;
    if (item.type === 'single_select' && item.answer.key === null) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['answer', 'key'],
        message: 'single_select answer must carry the chosen option key',
      });
    }
    if (item.type === 'number' && item.answer.key !== null) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['answer', 'key'],
        message: 'number answer must not carry an option key',
      });
    }
  });
export type BamResolvedItem = z.infer<typeof bamResolvedItemSchema>;

export const bamResolvedAssessmentSchema = z.object({
  assessment: z.object({
    assessmentId: z.string().uuid(),
    memberId: memberIdSchema,
    questionnaireId: z.string().uuid(),
    slug: z.string().min(1),
    version: z.number().int(),
    completedAt: z.string().datetime(),
    // Member-local completion day — see `bamCompletedAssessmentSummarySchema`.
    completedDate: checkInDateSchema,
  }),
  items: z.array(bamResolvedItemSchema),
});
export type BamResolvedAssessment = z.infer<typeof bamResolvedAssessmentSchema>;

export const internalBamAssessmentsResponseSchema = apiEnvelope(
  z.array(bamCompletedAssessmentSummarySchema),
);
// Atlas accepts older responses during deployment; the server contract still
// requires the field it always emits. safeExtend preserves the answer refinement.
export const internalBamAssessmentResponseSchema = apiEnvelope(bamResolvedAssessmentSchema);
const bamResolvedServerItemSchema = bamResolvedItemSchema.safeExtend({ skipped: z.boolean() });
const bamResolvedServerAssessmentSchema = bamResolvedAssessmentSchema.extend({
  items: z.array(bamResolvedServerItemSchema),
});
export type BamResolvedServerItem = z.infer<typeof bamResolvedServerItemSchema>;
export type BamResolvedServerAssessment = z.infer<typeof bamResolvedServerAssessmentSchema>;
export const internalBamAssessmentServerResponseSchema = apiEnvelope(
  bamResolvedServerAssessmentSchema,
);
